// Holiday tasks (WI-13b): the third way to earn twists, before and during the trip. The parent's
// list starts from STARTER_TASKS and is edited with `task.set` / `task.removed`; a child self-ticks a
// small task (`task.done`) or asks a grown-up (`task.asked` → Needs you → `task.done`). A task pays
// its twists once per child. Pure folds over the event log.
import type { Person } from '../../shared/events'
import { STARTER_TASKS, Task, TaskAsked, TaskDone, TaskSet } from '../../shared/rewards'
import type { Phase } from './release'

export interface PoolTask extends Task {
  id: string
}

interface Ev {
  id: string
  type: string
  at: number
  personId: string
  payload: unknown
}

const ordered = (events: readonly Ev[], types: string[]) => events.filter((e) => types.includes(e.type)).sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))

/** The family's task list for this trip (starter tasks, then the parent's edits). */
export function taskPool(events: readonly Ev[]): PoolTask[] {
  const rows = new Map<string, Record<string, unknown>>(Object.entries(STARTER_TASKS).map(([id, t]) => [id, { ...t }]))
  for (const e of ordered(events, ['task.set', 'task.removed'])) {
    if (e.type === 'task.set') {
      const p = TaskSet.safeParse(e.payload)
      if (p.success) rows.set(p.data.taskId, { ...rows.get(p.data.taskId), ...p.data.fields })
    } else rows.delete((e.payload as { taskId?: string } | null)?.taskId ?? '')
  }
  const out: PoolTask[] = []
  for (const [id, raw] of rows) {
    const t = Task.safeParse(raw)
    if (t.success) out.push({ ...t.data, id })
  }
  return out
}

const forWho = (t: Task, child: Pick<Person, 'id' | 'ageBand'>) => (t.who === 'all' ? true : Array.isArray(t.who) ? t.who.includes(child.id) : t.who === child.ageBand)

export type TaskState = 'todo' | 'asked' | 'done'

export interface ChildTask extends PoolTask {
  state: TaskState
}

/** A child's tasks right now: theirs, not paused, for this phase of the trip, with where each stands. */
export function tasksFor(events: readonly Ev[], child: Pick<Person, 'id' | 'ageBand'>, phase: Phase): ChildTask[] {
  const done = doneTasks(events, child.id)
  const asked = new Set(ordered(events, ['task.asked']).flatMap((e) => {
    const p = TaskAsked.safeParse(e.payload)
    return p.success && p.data.for === child.id ? [p.data.taskId] : []
  }))
  return taskPool(events)
    .filter((t) => !t.paused && forWho(t, child) && (t.when === 'any' || t.when === phase || (phase === 'after' && t.when === 'during' && done.has(t.id))))
    .map((t) => ({ ...t, state: done.has(t.id) ? 'done' : asked.has(t.id) ? 'asked' : 'todo' }))
}

/** Tasks done by a child: task id → the play-day it was done. */
export function doneTasks(events: readonly Ev[], childId: string): Map<string, string> {
  const out = new Map<string, string>()
  for (const e of ordered(events, ['task.done'])) {
    const p = TaskDone.safeParse(e.payload)
    if (p.success && p.data.for === childId && !out.has(p.data.taskId)) out.set(p.data.taskId, p.data.day)
  }
  return out
}

/** Tasks a child asked a grown-up to confirm and nobody has yet (for Needs you). */
export function askedTasks(events: readonly Ev[]): { key: string; taskId: string; childId: string; at: number; task: PoolTask | null }[] {
  const pool = new Map(taskPool(events).map((t) => [t.id, t]))
  const done = new Set(ordered(events, ['task.done']).flatMap((e) => {
    const p = TaskDone.safeParse(e.payload)
    return p.success ? [`${p.data.taskId}|${p.data.for}`] : []
  }))
  const out = new Map<string, { key: string; taskId: string; childId: string; at: number; task: PoolTask | null }>()
  for (const e of ordered(events, ['task.asked'])) {
    const p = TaskAsked.safeParse(e.payload)
    if (!p.success || done.has(`${p.data.taskId}|${p.data.for}`)) continue
    const key = `task:${p.data.taskId}:${p.data.for}`
    out.set(key, { key, taskId: p.data.taskId, childId: p.data.for, at: e.at, task: pool.get(p.data.taskId) ?? null })
  }
  return [...out.values()]
}
